import { forwardRef, useState, useRef, useMemo, useEffect } from "react";
import * as React from "react";
import { useTable, useFilters, useSortBy, type TableState } from "react-table";
import type { Cell, Column, FilterProps, TableOptions, ColumnInstance } from "react-table";
import { translate } from "../IntlProviderWrapper";
import { classNames } from "@/lib/util";

const COLUMN_WIDTH = 100;
const ROW_HEIGHT = 50;

export type VirtualTableColumn<T> = {
    class_name?: string;
    enableSorting?: boolean;
    fix?: boolean;
    title?: string;
} & Column<T>;

function generateSortingIndicator(column: ColumnInstance, show_unsorted = false) {
    if (!column.isSorted) {
        return show_unsorted ? <i className="fas fa-arrows-alt-v" /> : null;
    }

    return !column.isSortedDesc ? (
        <i className="fas fa-long-arrow-alt-down" />
    ) : (
        <i className="fas fa-long-arrow-alt-up" />
    );
}

function DefaultColumnFilter<T>(props: FilterProps<T>) {
    const {
        column: { filterValue, setFilter },
    } = props;

    function handleChange(event: React.SyntheticEvent<HTMLInputElement>) {
        setFilter(event.currentTarget.value || undefined);
    }

    return (
        <input
            className="form-control"
            type="text"
            value={filterValue || ""}
            onChange={handleChange}
            placeholder={translate("common.search", "Search")}
        />
    );
}

type Props<T> = {
    columns: VirtualTableColumn<T>[];
    data: T[];
    width?: number;
    height?: number;
    column_width?: number;
    head_height?: number;
    row_height?: number;
    foot_height?: number;
    fix_head?: boolean;
    fix_foot?: boolean;
    scroll_timeout?: number;
    rows_buffer?: number;
    columns_buffer?: number;
    class_name?: string;
    initialState?: TableState;
    getCellProps?: (cell: Cell<T>) => React.HTMLAttributes<HTMLDivElement>;
} & TableOptions<T>;

type State = {
    scroll_top: number;
    scroll_left: number;
    table_rect: DOMRect | null;
    table_client_width: number;
    table_client_height: number;
};

function VirtualTable<T>(props: Props<T>, ref: React.ForwardedRef<HTMLDivElement>) {
    const {
        columns,
        data,
        width,
        height,
        column_width = COLUMN_WIDTH,
        head_height = props.row_height || ROW_HEIGHT,
        row_height = ROW_HEIGHT,
        foot_height = props.row_height || ROW_HEIGHT,
        fix_head,
        fix_foot,
        scroll_timeout,
        rows_buffer = 0,
        columns_buffer = 0,
        class_name,
        initialState,
        getCellProps,
    } = props;
    const [{ scroll_top, scroll_left, table_rect, table_client_width, table_client_height }, setState] =
        useState<State>({
            scroll_top: 0,
            scroll_left: 0,
            table_rect: null,
            table_client_width: 0,
            table_client_height: 0,
        });
    const table_ref = useRef<HTMLDivElement | null>(null);
    const scroll_timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const animation_frame = useRef<ReturnType<typeof requestAnimationFrame> | null>(null);
    const default_column = useMemo(
        () => ({
            Filter: DefaultColumnFilter,
        }),
        []
    );
    const table = useTable(
        {
            columns,
            data,
            initialState,
            defaultColumn: default_column,
        },
        useFilters,
        useSortBy
    );

    useEffect(() => {
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    function getWidth() {
        if (width !== undefined) {
            return width;
        }

        if (table_rect !== null) {
            return table_rect.width;
        }

        return 0;
    }

    function getHeight() {
        if (height !== undefined) {
            return height;
        }

        if (table_rect) {
            return table_rect.height;
        }

        return 0;
    }

    function getColumnWidth(column: ColumnInstance): number {
        let w = column_width;
        if (typeof column.width === "number") {
            w = column.width;
        } else if (typeof column.width === "string") {
            w = parseFloat(column.width);
            if (column.width.trim().endsWith("%")) {
                w = (w / 100) * table_client_width;
            }
        }

        if (column.minWidth !== undefined && w < column.minWidth) {
            w = column.minWidth;
        }

        if (column.maxWidth !== undefined && w > column.maxWidth) {
            w = column.maxWidth;
        }

        return w;
    }

    function getTotalWidth(): number {
        let total_width = 0;
        table.columns.forEach((column: ColumnInstance) => {
            total_width += getColumnWidth(column);
        });
        return total_width;
    }

    function getTotalHeight(): number {
        return head_height + foot_height + row_height * table.rows.length;
    }

    function getCellBounds(): [number, number, number, number] {
        const end_width = scroll_left + getWidth();
        const row_count = table.rows.length;

        const start_row = Math.max(0, Math.floor((scroll_top - head_height) / row_height) - rows_buffer);
        const end_row = Math.min(
            Math.max(0, Math.ceil((scroll_top - head_height + getHeight()) / row_height) - 1) + rows_buffer,
            row_count - 1
        );

        const column_count = table.columns.length;
        let start_column = -1;
        let end_column = column_count - 1;

        let column_offset = 0;
        for (let i = 0; i < column_count; i++) {
            const column = table.columns[i];
            column_offset += getColumnWidth(column);

            if (start_column < 0 && column_offset > scroll_left) {
                start_column = i;
            }

            if (column_offset > end_width) {
                end_column = i;
                break;
            }
        }

        start_column = Math.max(0, start_column - columns_buffer);
        end_column = Math.min(column_count - 1, end_column + columns_buffer);

        return [start_column, start_row, end_column, end_row];
    }

    function handleResize() {
        if (table_ref.current !== null) {
            setState(prev_state => ({
                ...prev_state,
                table_rect: table_ref.current ? table_ref.current.getBoundingClientRect() : null,
                table_client_width: table_ref.current ? table_ref.current.clientWidth : 0,
                table_client_height: table_ref.current ? table_ref.current.clientHeight : 0,
            }));
        }
    }

    function handleScroll(event: React.SyntheticEvent<HTMLDivElement>) {
        const target_scroll_top = event.currentTarget.scrollTop;
        const target_scroll_left = event.currentTarget.scrollLeft;

        if (scroll_timeout !== undefined) {
            clearTimeout(scroll_timer.current);
            scroll_timer.current = setTimeout(() => {
                setState(prev_state => ({
                    ...prev_state,
                    scroll_top: target_scroll_top,
                    scroll_left: target_scroll_left,
                }));
            }, scroll_timeout);
        } else {
            if (animation_frame.current !== null) {
                cancelAnimationFrame(animation_frame.current);
            }
            animation_frame.current = requestAnimationFrame(() => {
                setState(prev_state => ({
                    ...prev_state,
                    scroll_top: target_scroll_top,
                    scroll_left: target_scroll_left,
                }));
            });
        }
    }

    function renderTable() {
        const [start_column, start_row, end_column, end_row] = getCellBounds();
        const offset_left = table.columns.slice(0, start_column).reduce((offset: number, column: { fix: boolean }) => {
            if (column.fix) {
                return offset;
            }

            return offset + getColumnWidth(column);
        }, 0);

        const thead: React.ReactNode[] = [];
        const thead_offset = {
            top: fix_head ? scroll_top : 0,
            left: offset_left,
        };
        if (scroll_top < head_height || fix_head) {
            const cells: React.ReactNode[] = [];
            for (let j = 0; j < table.columns.length; j++) {
                const column = table.columns[j];
                if (!column.fix && (j < start_column || j > end_column)) {
                    continue;
                }

                const column_width = getColumnWidth(column);
                const header_props = column.getHeaderProps(column.enableSorting ? column.getSortByToggleProps() : {});

                if (column.title !== undefined) {
                    header_props.title = column.title;
                } else if (column.enableSorting) {
                    header_props.title = column.canFilter ? "" : translate("common.toggle_sorting", "Toggle sorting");
                }

                cells.push(
                    <div
                        {...header_props}
                        className={classNames(`virtual-table-th virtual-table-column-${column.id}`, column.class_name, {
                            "is-fixed": column.fix,
                        })}
                        style={{
                            ...header_props.style,
                            transform: column.fix ? `translateX(${scroll_left - thead_offset.left}px)` : "none",
                            width: column_width,
                            height: head_height,
                        }}
                    >
                        <div>
                            {column.render("Header")}
                            {column.canFilter ? column.render("Filter") : null}
                            {/** Use fragment to handle leading space in sorting indicator */}
                            {column.enableSorting && !column.canFilter && <> {generateSortingIndicator(column)}</>}
                        </div>
                    </div>
                );
            }

            const header_group_props = table.headerGroups[0].getHeaderGroupProps();
            thead.push(
                <div {...header_group_props} className="virtual-table-tr">
                    {cells}
                </div>
            );
        }

        const tbody: React.ReactNode[] = [];
        const tbody_offset = {
            top: head_height + row_height * start_row,
            left: offset_left,
        };

        for (let i = start_row; i <= end_row; i++) {
            const row = table.rows[i];
            table.prepareRow(row);

            const cells: React.ReactNode[] = [];
            for (let j = 0; j < table.columns.length; j++) {
                const column = table.columns[j];
                if (!column.fix && (j < start_column || j > end_column)) {
                    continue;
                }

                const column_width = getColumnWidth(column);
                const cell = row.cells[j];
                const custom_cell_props = getCellProps ? getCellProps(cell) : {};
                const cell_props = cell.getCellProps();
                cells.push(
                    <div
                        {...cell_props}
                        {...custom_cell_props}
                        className={classNames(
                            `virtual-table-td virtual-table-column-${column.id}`,
                            custom_cell_props.className,
                            {
                                "is-fixed": column.fix,
                            }
                        )}
                        style={{
                            transform: column.fix ? `translateX(${scroll_left - tbody_offset.left}px)` : "none",
                            width: column_width,
                            height: row_height,
                            ...custom_cell_props.style,
                        }}
                    >
                        {cell.render("Cell")}
                    </div>
                );
            }

            const row_props = row.getRowProps();
            tbody.push(
                <div {...row_props} className="virtual-table-tr">
                    {cells}
                </div>
            );
        }

        const tfoot: React.ReactNode[] = [];
        const max_offset_top = head_height + row_height * table.rows.length;
        const tfoot_offset = {
            top: fix_foot ? Math.min(scroll_top + table_client_height - foot_height, max_offset_top) : max_offset_top,
            left: offset_left,
        };
        if (scroll_top + getHeight() > max_offset_top || fix_foot) {
            const cells: React.ReactNode[] = [];
            for (let j = 0; j < table.columns.length; j++) {
                const column = table.columns[j];
                if (!column.fix && (j < start_column || j > end_column)) {
                    continue;
                }

                const column_width = getColumnWidth(column);
                const footer_props = column.getFooterProps();

                cells.push(
                    <div
                        {...footer_props}
                        className={classNames(`virtual-table-td virtual-table-column-${column.id}`, column.class_name, {
                            "is-fixed": column.fix,
                        })}
                        style={{
                            ...footer_props.style,
                            transform: column.fix ? `translateX(${scroll_left - tfoot_offset.left}px)` : "none",
                            width: column_width,
                            height: foot_height,
                        }}
                    >
                        {column.render("Footer")}
                    </div>
                );
            }

            const footer_group_props = table.footerGroups[0].getFooterGroupProps();
            tfoot.push(
                <div {...footer_group_props} className="virtual-table-tr">
                    {cells}
                </div>
            );
        }

        const table_props = table.getTableProps();
        const table_body_props = table.getTableBodyProps();

        return (
            <div
                {...table_props}
                className={classNames("virtual-table-container", table_props.className)}
                style={{
                    ...table_props.style,
                    position: "relative",
                    width: getTotalWidth(),
                    height: getTotalHeight(),
                }}
            >
                <div
                    className={classNames("virtual-table-thead", {
                        "is-fixed": fix_head,
                    })}
                    style={{
                        position: "absolute",
                        zIndex: 1,
                        transform: `translate(${thead_offset.left}px, ${thead_offset.top}px)`,
                    }}
                >
                    {thead}
                </div>
                <div
                    {...table_body_props}
                    className={`virtual-table-tbody${
                        table_body_props.className ? ` ${table_body_props.className}` : ""
                    }`}
                    style={{
                        ...table_body_props.style,
                        position: "absolute",
                        transform: `translate(${tbody_offset.left}px, ${tbody_offset.top}px)`,
                    }}
                >
                    {tbody}
                </div>
                {tfoot.length > 0 && (
                    <div
                        className={classNames("virtual-table-tfoot", {
                            "is-fixed": fix_foot,
                        })}
                        style={{
                            position: "absolute",
                            transform: `translate(${tfoot_offset.left}px, ${tfoot_offset.top}px)`,
                        }}
                    >
                        {tfoot}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div
            ref={tr => {
                if (typeof ref === "function") {
                    ref(tr);
                } else if (ref !== null) {
                    ref.current = tr;
                }

                table_ref.current = tr;

                if (tr === null) {
                    return;
                }

                const new_table_rect = tr.getBoundingClientRect();
                const new_table_client_width = tr.clientWidth;
                const new_table_client_height = tr.clientHeight;
                if (
                    table_client_width !== new_table_client_width ||
                    table_client_height !== new_table_client_height ||
                    JSON.stringify(table_rect) !== JSON.stringify(new_table_rect)
                ) {
                    setState(prev_state => ({
                        ...prev_state,
                        table_rect: new_table_rect,
                        table_client_width: new_table_client_width,
                        table_client_height: new_table_client_height,
                    }));
                }
            }}
            className={classNames("virtual-table", class_name)}
            style={{ width: getWidth(), height: getHeight(), overflow: "auto" }}
            onScroll={handleScroll}
        >
            {renderTable()}
        </div>
    );
}

export default forwardRef<HTMLDivElement, Props<unknown>>(VirtualTable);
